const WORKBOX_CACHE_PREFIX = "workbox-"

export type NativeServiceWorkerCleanupResult = {
    registrationsFound: number
    registrationsRemoved: number
    cachesRemoved: number
}

/**
 * Capacitor serves bundled files from a browser-like local origin, but those
 * files must not be controlled by the web/PWA service worker. Remove state
 * left behind by releases that registered Workbox inside the native WebView.
 */
export async function cleanupNativeServiceWorkerState(
    serviceWorker:
        | Pick<ServiceWorkerContainer, "getRegistrations">
        | undefined = navigator.serviceWorker,
    cacheStorage: Pick<CacheStorage, "keys" | "delete"> | undefined = globalThis.caches,
): Promise<NativeServiceWorkerCleanupResult> {
    const registrations = serviceWorker ? await serviceWorker.getRegistrations() : []
    const unregisterResults = await Promise.allSettled(
        registrations.map((registration) => registration.unregister()),
    )

    const cacheNames = cacheStorage ? await cacheStorage.keys() : []
    const workboxCacheNames = cacheNames.filter((name) => name.startsWith(WORKBOX_CACHE_PREFIX))
    const cacheDeleteResults = cacheStorage
        ? await Promise.allSettled(workboxCacheNames.map((name) => cacheStorage.delete(name)))
        : []

    return {
        registrationsFound: registrations.length,
        registrationsRemoved: unregisterResults.filter(
            (result) => result.status === "fulfilled" && result.value,
        ).length,
        cachesRemoved: cacheDeleteResults.filter(
            (result) => result.status === "fulfilled" && result.value,
        ).length,
    }
}

type ServiceWorkerUpdateOptions = {
    online?: boolean
    fetcher?: typeof fetch
    warn?: (...data: unknown[]) => void
}

export type ServiceWorkerUpdateResult = "updated" | "skipped" | "unavailable" | "failed"

/**
 * Check that the worker script is reachable before asking the browser to
 * update the registration. Network and browser lifecycle errors are expected
 * here and should not become unhandled promise rejections.
 */
export async function updateServiceWorkerIfAvailable(
    swUrl: string,
    registration: ServiceWorkerRegistration,
    {
        online = navigator.onLine,
        fetcher = globalThis.fetch,
        warn = console.warn,
    }: ServiceWorkerUpdateOptions = {},
): Promise<ServiceWorkerUpdateResult> {
    if (!online || registration.installing) return "skipped"

    try {
        const response = await fetcher(swUrl, {
            cache: "no-store",
            headers: {
                cache: "no-store",
                "cache-control": "no-cache",
            },
        })

        if (!response.ok) return "unavailable"

        await registration.update()
        return "updated"
    } catch (error) {
        warn("Service worker update check failed", error)
        return "failed"
    }
}
